/**
 * Compares two semantic version strings to determine if the first is greater than or equal to the second
 * @param currentVersion - The current version to check
 * @param requiredVersion - The minimum required version
 * @returns true if currentVersion >= requiredVersion, false otherwise
 */
const compareVersions = (
  currentVersion: string,
  requiredVersion: string
): boolean => {
  const parseVersion = (version: string): number[] => {
    return version.split(".").map((part) => parseInt(part, 10) || 0);
  };

  const current = parseVersion(currentVersion);
  const required = parseVersion(requiredVersion);

  // Ensure both arrays have the same length by padding with zeros
  const maxLength = Math.max(current.length, required.length);
  while (current.length < maxLength) current.push(0);
  while (required.length < maxLength) required.push(0);

  // Compare each part
  for (let i = 0; i < maxLength; i++) {
    if (current[i] > required[i]) return true;
    if (current[i] < required[i]) return false;
  }

  return true; // Equal versions are considered allowed
};

/**
 * Core function to check if a Salesforce feature is allowed based on managed package version
 * @param managedPackageCurrentVersion - Current version of the managed package (e.g., "1.5.0")
 * @param requiredVersion - Minimum version required for the feature
 * @returns true if feature is allowed, false otherwise
 */
export function isSalesforceFeatureAllowed(
  managedPackageCurrentVersion: string | undefined,
  requiredVersion: string
): boolean {
  // If no version info, assume it's allowed (fallback)
  if (!managedPackageCurrentVersion) return true;

  try {
    // Use our custom version comparison
    return compareVersions(managedPackageCurrentVersion, requiredVersion);
  } catch (error) {
    // If version parsing fails, allow by default
    console.warn("Failed to parse managed package version:", error);
    return true;
  }
}
